// /DeviceConfigs/DeviceConfigEditFormContainer.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import axios from '../../config/axiosInstance';
import { Form } from 'antd';
import useDeviceConfigData from './DeviceConfigFormModule/useDeviceConfigData';
import { buildInitialFormValues } from './helpers/buildInitialFormValues';
import DeviceConfigEditFormInner from './DeviceConfigEditFormInner';
import { createHandleEventChange } from './helpers/eventChangeHandler';

// This container isolates data-fetching + initial snapshot build
const DeviceConfigEditFormContainer = ({ open, initialValues, onCancel, onFinish, admin = false }) => {
    const [form] = Form.useForm();

    const {
        events,
        programmePoints,
        locations,
        participantTypes,
        customFields,
        fetchEvents,
        fetchEventDetails,
        fetchToken,
        setProgrammePoints,
        setLocations,
        setParticipantTypes,
        setCustomFields,
    } = useDeviceConfigData({ admin });

    const [formInitialSnapshot, setFormInitialSnapshot] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [showEFN, setShowEFN] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState('event');
    const [isProgrammePointDisabled, setIsProgrammePointDisabled] = useState(false);
    const [isLocationDisabled, setIsLocationDisabled] = useState(false);

    // 1) load events when mounting
    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // 2) When the modal opens, we build the initial snapshot only once.
    useEffect(() => {
        if (!open) return;

        // build snapshot
        const { initialObj, showEFN: efnFlag } = buildInitialFormValues(initialValues, events);

        setFormInitialSnapshot(initialObj);
        setShowEFN(efnFlag || false);
        setActiveTabKey('event');

        // selectedEventId comes from the snapshot, not from the raw prop
        setSelectedEventId(initialObj.SelectedEventId || null);

        // enable/disable PP / Location combos
        setIsProgrammePointDisabled(!!initialObj.SelectedLocationId);
        setIsLocationDisabled(!!initialObj.SelectedProgrammePointId);

        // set the initial snapshot in the form
        form.setFieldsValue(initialObj);
        handleEventChange(initialObj.SelectedEventId || null);

        // Synchronise the base field with its variant according to type
        const currentType = initialObj.Type;
        const createLeadValue = initialObj.CreateLeadImmediately;

        if (createLeadValue !== undefined && currentType) {
            const dynamicField = `CreateLeadImmediately_${currentType}`;
            form.setFieldValue(dynamicField, createLeadValue);
        }

    }, [open, initialValues, events, form]);

    // 3) handler: user changes the event in the form
    const handleEventChange = useMemo(
        () =>
            createHandleEventChange({
                form,
                events,
                setSelectedEventId,
                setProgrammePoints,
                setLocations,
                setParticipantTypes,
                setCustomFields,
                setShowEFN,
                fetchToken,
                fetchEventDetails,
            }),
        [
            form,
            events,
            setSelectedEventId,
            setProgrammePoints,
            setLocations,
            setParticipantTypes,
            setCustomFields,
            setShowEFN,
            fetchToken,
            fetchEventDetails,
        ]
    );

    // 4) Save
    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();

            // if no SelectedProgrammePointId => force IsCreateLeadEnabled = false, etc.
            // we sanitize before sending to backend so UI never auto-toggles
            const deviceType = (values.Type ?? '').toString().trim().toLowerCase();

            if (deviceType === 'mobile') {
                values.CreateLeadImmediately = values.CreateLeadImmediately_mobile;
            } else if (deviceType === 'leadcapture') {
                values.CreateLeadImmediately = values.CreateLeadImmediately_leadcapture;
                values.Mode = 'leadcapture';
            } else if (deviceType === 'desktop') {
                values.Mode = 'selfCheckIn';
            }

            if (!values.SelectedProgrammePointId) {
                values.IsCreateLeadEnabled = false;
                values.CreateLeadImmediately = false;
            }

            // participantTypesDisabled
            const checkedParticipantTypes = Object.keys(values)
                .filter(k => k.startsWith('participantType_') && values[k])
                .map(k => k.replace('participantType_', ''));

            // Remove the "participantType_*" keys from the payload.
            const cleanValues = Object.fromEntries(
                Object.entries(values).filter(([k]) => !k.startsWith('participantType_'))
            );

            // SelectedProgrammePointName
            const selectedPoint = programmePoints.find(p => p.id === values.SelectedProgrammePointId);
            const selectedPointName = selectedPoint ? selectedPoint.name?.[0]?.value : null;

            // CustomFields expanded
            const selectedCustomFieldGroups = (values.CustomSeatFieldIds || []).map(groupId => {
                const group = customFields.find(g => g.id === groupId);
                return {
                    groupId,
                    groupName: group?.name?.[0]?.value || 'Unnamed',
                    fields: (group?.participantCustomFields?.participantCustomField || []).map(field => ({
                        id: field.id,
                        name: field.name,
                        customFieldTypeID: field.customFieldTypeID,
                        position: field.position,
                    }))
                };
            });

            const serverOverride = values.ServerOverrideUrl?.trim() || null;

            const payload = {
                ...cleanValues,
                gdprText: cleanValues.IsSignatureEnabled ? cleanValues.gdprText : "",
                participantTypesDisabled: checkedParticipantTypes,
                SelectedProgrammePointName: selectedPointName,
                CustomFields: selectedCustomFieldGroups,
                Theme: values.theme ? 'dark' : 'light',
                ServerOverrideUrl: serverOverride,
            };

            const baseURL = admin ? '/deviceConfigs/admin' : '/deviceConfigs';

            if (initialValues && initialValues._id) {
                await axios.put(`${baseURL}/${initialValues._id}`, payload);
                message.success(`DeviceConfig "${values.DeviceConfigName}" updated successfully.`);
            } else {
                await axios.post(baseURL, payload);
                message.success(`DeviceConfig "${values.DeviceConfigName}" added successfully.`);
            }

            onFinish();
            onCancel();
        } catch (err) {
            console.error('Error saving DeviceConfig:', err);
            message.error('Error saving DeviceConfig. Please try again.');
        }
    }, [
        form,
        programmePoints,
        customFields,
        admin,
        initialValues,
        onFinish,
        onCancel
    ]);

    const handleClose = useCallback(() => {
        onCancel();
    }, [onCancel]);

    return (
        <DeviceConfigEditFormInner
            // form + init snapshot
            form={form}
            open={open}
            initialFormValues={formInitialSnapshot || {}}

            // state
            activeTabKey={activeTabKey}
            setActiveTabKey={setActiveTabKey}
            selectedEventId={selectedEventId}
            setSelectedEventId={setSelectedEventId}
            showEFN={showEFN}
            isProgrammePointDisabled={isProgrammePointDisabled}
            isLocationDisabled={isLocationDisabled}

            // data collections
            events={events}
            programmePoints={programmePoints}
            locations={locations}
            participantTypes={participantTypes}
            customFields={customFields}

            // handlers
            handleEventChange={handleEventChange}
            handleSave={handleSave}
            handleClose={handleClose}
            admin={admin}
        />
    );
};

export default DeviceConfigEditFormContainer;